/**
 * Document helper utilities
 * Provides functions for file validation and path extraction
 */

import { File as FileEnum } from "@funded-here-interface/common/src/constant/enum";
import { UploadedFile } from "../../../features/merchant/doc";

/**
 * Extract file paths from uploaded files array
 */
export const extractFilePaths = (files: UploadedFile[]): string[] => {
  return files.map((f) => f.path);
};

/**
 * Validate required documents are uploaded
 * Returns array of missing document names
 */
export const validateRequiredDocuments = (documents: {
  businessProfile: UploadedFile[];
  certificateOfIncorporation: UploadedFile[];
  proofOfBankAccountDetails: UploadedFile[];
  supportingDocumentsWithSuppliers: UploadedFile[];
}): string[] => {
  return SUPPLIER_DOCUMENT_CONFIG.filter(
    ({ docKey }) => documents[docKey].length === 0
  ).map(({ displayName }) => displayName.toLowerCase());
};

/**
 * Validation configuration for supplier documents
 * Maps document keys to their error keys and display names
 */
const SUPPLIER_DOCUMENT_CONFIG = [
  {
    docKey: "businessProfile" as const,
    errorKey: "businessProfileFile",
    displayName: "Company/Business Profile",
  },
  {
    docKey: "certificateOfIncorporation" as const,
    errorKey: "certificateOfIncorporation",
    displayName: "Certificate of Incorporation",
  },
  {
    docKey: "proofOfBankAccountDetails" as const,
    errorKey: "proofOfBankAccountDetails",
    displayName: "Proof of Bank Account Details",
  },
  {
    docKey: "supportingDocumentsWithSuppliers" as const,
    errorKey: "supportingDocumentsWithSuppliers",
    displayName: "Additional Supporting Documents",
  },
];

/**
 * Validation configuration for institutional investor documents
 */
const INSTITUTIONAL_INVESTOR_DOCUMENT_CONFIG = [
  {
    docKey: "certificateOfIncorporation" as const,
    displayName: "certificate of incorporation",
  },
  {
    docKey: "businessProfile" as const,
    displayName: "company/business Profile",
  },
  {
    docKey: "companyConstitution" as const,
    displayName: "company constitution file",
  },
  {
    docKey: "proofOfAddress" as const,
    displayName: "proof of residential address",
  },
  {
    docKey: "financialStatement" as const,
    displayName: "financial statement file",
  },
  {
    docKey: "proofOfBankAccountDetails" as const,
    displayName: "proof of bank account details file",
  },
];

/**
 * Validation configuration for individual investor documents
 */
const INDIVIDUAL_INVESTOR_DOCUMENT_CONFIG = [
  {
    docKey: "proofOfAddress" as const,
    displayName: "proof of residential address",
  },
  { docKey: "salaryProof" as const, displayName: "salary proof file" },
  {
    docKey: "bankStatement" as const,
    displayName: "proof of bank account details",
  },
];

/**
 * Validation configuration for borrower documents
 */
const BORROWER_DOCUMENT_CONFIG = [
  {
    docKey: "businessProfile" as const,
    displayName: "company/business Profile",
  },
  {
    docKey: "bankStatement" as const,
    displayName: "proof of bank account details",
  },
  {
    docKey: "proofOfAddress" as const,
    displayName: "proof of residential address",
  },
  {
    docKey: "balanceSheetPAndLStatement" as const,
    displayName: "financial documents",
  },
  {
    docKey: "certificateOfIncorporation" as const,
    displayName: "certificate of incorporation",
  },
  {
    docKey: "supportingDocumentsWithSuppliers" as const,
    displayName: "supporting documents with suppliers",
  },
];

/**
 * Validate required supplier documents and return errors object
 * Returns object with error keys matching FileUploadComponent config
 */
export const validateSupplierDocumentsWithErrors = (documents: {
  businessProfile: UploadedFile[];
  certificateOfIncorporation: UploadedFile[];
  proofOfBankAccountDetails: UploadedFile[];
  supportingDocumentsWithSuppliers: UploadedFile[];
}): { [key: string]: string } => {
  const errors: { [key: string]: string } = {};

  SUPPLIER_DOCUMENT_CONFIG.forEach(({ docKey, errorKey, displayName }) => {
    if (documents[docKey].length === 0) {
      errors[errorKey] = `${displayName} is required`;
    }
  });

  return errors;
};

/**
 * Get existing files for a specific file type (Supplier documents)
 * Helper to simplify the existingFiles prop logic
 */
export const getExistingFilesForType = (
  fileId: FileEnum,
  documents: {
    businessProfile: UploadedFile[];
    certificateOfIncorporation: UploadedFile[];
    proofOfBankAccountDetails: UploadedFile[];
    supportingDocumentsWithSuppliers: UploadedFile[];
  }
): UploadedFile[] => {
  switch (fileId) {
    case FileEnum.BUSINESS_PROFILE:
      return documents.businessProfile;
    case FileEnum.CERTIFICATE_OF_INCORPORATION:
      return documents.certificateOfIncorporation;
    case FileEnum.PROOF_OF_BANK_ACCOUNT_DETAILS:
      return documents.proofOfBankAccountDetails;
    case FileEnum.SUPPORTING_DOCUMENTS_WITH_SUPPLIERS:
      return documents.supportingDocumentsWithSuppliers;
    default:
      return [];
  }
};

/**
 * Validate required institutional investor documents are uploaded
 * Returns array of missing document names
 *
 * @param documents - All institutional investor documents
 * @param options - Validation options
 * @param options.includeCrsSelfCertification - Whether to validate CRS form (true for INVESTOR, false for ISPV)
 */
export const validateRequiredInstitutionalInvestorDocuments = (
  documents: {
    businessProfile: UploadedFile[];
    companyConstitution: UploadedFile[];
    certificateOfIncorporation: UploadedFile[];
    crsSelfCertificationForm: UploadedFile[];
    proofOfBankAccountDetails: UploadedFile[];
    financialStatement: UploadedFile[];
    proofOfAddress: UploadedFile[];
  },
  options: {
    includeCrsSelfCertification: boolean;
  }
): string[] => {
  const missingFiles = INSTITUTIONAL_INVESTOR_DOCUMENT_CONFIG.filter(
    ({ docKey }) => documents[docKey].length === 0
  ).map(({ displayName }) => displayName);

  // CRS Self-Certification Form is conditional (required for Investors, not for ISPV)
  if (
    options.includeCrsSelfCertification &&
    documents.crsSelfCertificationForm.length === 0
  ) {
    missingFiles.push("CRS self-certification form file");
  }

  return missingFiles;
};

/**
 * Get existing files for a specific file type (Investor documents)
 * Replaces the complex nested ternary operator in InvestorDocsUpload
 */
export const getExistingFilesForInvestorType = (
  fileId: FileEnum,
  documents: {
    businessProfile: UploadedFile[];
    companyConstitution: UploadedFile[];
    certificateOfIncorporation: UploadedFile[];
    crsSelfCertificationForm: UploadedFile[];
    proofOfBankAccountDetails: UploadedFile[];
    financialStatement: UploadedFile[];
    proofOfAddress: UploadedFile[];
    shareholdersDirectorsRegisters: UploadedFile[];
    investmentMandate: UploadedFile[];
    fundManagerLicense: UploadedFile[];
  }
): UploadedFile[] => {
  switch (fileId) {
    case FileEnum.BUSINESS_PROFILE:
      return documents.businessProfile;
    case FileEnum.COMPANY_CONSTITUTION:
      return documents.companyConstitution;
    case FileEnum.CERTIFICATE_OF_INCORPORATION:
      return documents.certificateOfIncorporation;
    case FileEnum.CRS_SELF_CERTIFICATION_FORM:
      return documents.crsSelfCertificationForm;
    case FileEnum.PROOF_OF_BANK_ACCOUNT_DETAILS:
      return documents.proofOfBankAccountDetails;
    case FileEnum.FINANCIAL_STATEMENT:
      return documents.financialStatement;
    case FileEnum.PROOF_OF_ADDRESS:
      return documents.proofOfAddress;
    case FileEnum.SHAREHOLDERS_DIRECTORS_REGISTERS:
      return documents.shareholdersDirectorsRegisters;
    case FileEnum.INVESTMENT_MANDATE:
      return documents.investmentMandate;
    case FileEnum.FUND_MANAGER_LICENSE:
      return documents.fundManagerLicense;
    default:
      return [];
  }
};

/**
 * Validate required individual investor documents are uploaded
 * Returns array of missing document names
 */
export const validateRequiredIndividualInvestorDocuments = (documents: {
  proofOfAddress: UploadedFile[];
  salaryProof: UploadedFile[];
  bankStatement: UploadedFile[];
}): string[] => {
  return INDIVIDUAL_INVESTOR_DOCUMENT_CONFIG.filter(
    ({ docKey }) => documents[docKey].length === 0
  ).map(({ displayName }) => displayName);
};

/**
 * Get existing files for a specific file type (Individual Investor documents)
 * Replaces the complex nested ternary operator in IndInvestorDoc
 */
export const getExistingFilesForIndividualInvestorType = (
  fileId: FileEnum,
  documents: {
    proofOfAddress: UploadedFile[];
    salaryProof: UploadedFile[];
    bankStatement: UploadedFile[];
  }
): UploadedFile[] => {
  switch (fileId) {
    case FileEnum.PROOF_OF_ADDRESS:
      return documents.proofOfAddress;
    case FileEnum.SALARY_PROOF:
      return documents.salaryProof;
    case FileEnum.BANK_STATEMENT:
      return documents.bankStatement;
    default:
      return [];
  }
};

/**
 * Validate required borrower documents are uploaded
 * Returns array of missing document names
 */
export const validateRequiredBorrowerDocuments = (documents: {
  businessProfile: UploadedFile[];
  bankStatement: UploadedFile[];
  proofOfAddress: UploadedFile[];
  balanceSheetPAndLStatement: UploadedFile[];
  certificateOfIncorporation: UploadedFile[];
  supportingDocumentsWithSuppliers: UploadedFile[];
}): string[] => {
  return BORROWER_DOCUMENT_CONFIG.filter(
    ({ docKey }) => documents[docKey].length === 0
  ).map(({ displayName }) => displayName);
};

/**
 * Get existing files for a specific file type (Borrower documents)
 * Replaces the complex nested ternary operator in BorrowerDoc
 */
export const getExistingFilesForBorrowerType = (
  fileId: FileEnum,
  documents: {
    businessProfile: UploadedFile[];
    shareholdersDirectorsRegisters: UploadedFile[];
    proofOfAddress: UploadedFile[];
    bankStatement: UploadedFile[];
    balanceSheetPAndLStatement: UploadedFile[];
    certificateOfIncorporation: UploadedFile[];
    supportingDocumentsWithSuppliers: UploadedFile[];
  }
): UploadedFile[] => {
  switch (fileId) {
    case FileEnum.CERTIFICATE_OF_INCORPORATION:
      return documents.certificateOfIncorporation;
    case FileEnum.PROOF_OF_ADDRESS:
      return documents.proofOfAddress;
    case FileEnum.SHAREHOLDERS_DIRECTORS_REGISTERS:
      return documents.shareholdersDirectorsRegisters;
    case FileEnum.BANK_STATEMENT:
      return documents.bankStatement;
    case FileEnum.BUSINESS_PROFILE:
      return documents.businessProfile;
    case FileEnum.BALANCE_SHEET:
      return documents.balanceSheetPAndLStatement;
    case FileEnum.SUPPORTING_DOCUMENTS_WITH_SUPPLIERS:
      return documents.supportingDocumentsWithSuppliers;
    default:
      return [];
  }
};
